import { zodResolver } from '@hookform/resolvers/zod';
import {
  type TicketResource,
  TicketVoidsController_void_v1,
  type TicketVoidsController_void_v1Body,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { requiredVoidReasonSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';
import { formatPaise } from '@/lib/money';
import { newUuidv7 } from '@/lib/uuidv7';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';

/**
 * Voiding a ticket from the back office: `POST /v1/tickets/{id}/void`.
 *
 * **A void is a new record and never an edit.** The ticket row is append-only on
 * the server, so nothing here changes the sale — what is written is a separate
 * record pointing at it. The ticket stays readable everywhere, marked, and its
 * number is never reissued, because the artefact still exists in the world and
 * somebody is holding it.
 *
 * ## Who may do this
 *
 * Anyone signed in who can reach the ticket. `TicketVoidsController` declares
 * `@RequireRole()` with **no** arguments and `@RequireOutletScope()`, and the
 * comment there says why it is not a list of roles: the rule is about *reach*
 * rather than rank — anyone who may issue at an outlet may void there until it
 * closes. So there is no role check in this dialog and there must not be one; a
 * screen inventing a rank here would hide a control the API grants.
 *
 * A ticket outside the caller's reach answers `404`, byte for byte identical to
 * one that does not exist (FR-128). The two are not told apart here for the same
 * reason `Ticket` does not tell them apart on the read: a distinguishable
 * refusal, repeated across a range of ids, is a map of which outlets are selling.
 *
 * ## The reason is required here, and that is this screen's decision
 *
 * `VoidTicketDto` makes `reason` optional and the server accepts one without it.
 * This asks for it anyway, and says so on screen, because a back-office void is
 * the one nobody will be able to reconstruct: it is made at a desk, possibly
 * days later, about a ticket somebody else sold, and a day's voids with no
 * reasons is a bad conversation at close.
 *
 * **That argument was sound and, when it was written, its premise was false.**
 * `TicketResource` carried `voidedAt` and no reason at all, so this screen was
 * compelling somebody to justify an action on the explicit grounds that it
 * would be read at close — and nothing could read it. The ticket void was the
 * only reason-taking write in the system with no resource that gave the reason
 * back. `blossom-rest-api#375` closed that: `voidReason` is on the resource,
 * and FE053 puts it on the record and on the row.
 *
 * **The compulsion stays, and it is now the argument it always claimed to be.**
 * Nothing about the situation changed — a desk void is still the one nobody can
 * reconstruct — and what was missing was the reader, not the reason. The one
 * thing that would have argued for dropping it is if the reason were still
 * write-only, and the honest answer then would have been to stop asking rather
 * than to keep asking into a void. See `ticket-void.ts` for where it is read.
 *
 * **The till does not insist**, deliberately — there is a visitor waiting for
 * every sentence typed at a gate, and a mis-key corrected ten seconds later
 * nearly explains itself. The two screens differ because their situations do,
 * not because one of them forgot. That asymmetry is why a null reason is an
 * ordinary record rather than a hole in one, and why no screen may render it as
 * a blank.
 *
 * ## Two client-minted ids, and they are not the same id
 *
 * - **`body.id` is the void's own**, a fresh UUIDv7 per void. Sending one already
 *   recorded against a *different* ticket is `409 TICKETING_VOID_IMMUTABLE`,
 *   non-retryable, and means the client reused an id.
 * - **`Idempotency-Key` is per attempt**, and makes a retry of *this* void replay
 *   the first answer rather than write again.
 *
 * Both are held for as long as this form is mounted, which is exactly one void:
 * the dialog unmounts when it closes, so the next one gets fresh values, and a
 * retry after a dropped answer carries the same pair. Minting either inside the
 * retry is the mistake — a fresh void id on a retry of one void is what turns a
 * replay into a second record's worth of trouble.
 *
 * ## `200` is success and this screen never says otherwise
 *
 * A ticket already voided answers `200` with the original outcome, and `201` is a
 * void this request wrote. The copy below is true of either: the ticket is
 * voided.
 *
 * **Flattening them is the decision here, not the default.** `RequestExtras.onStatus`
 * would report which status carried the body, and this screen deliberately does
 * not ask. A `200` on this route is not "you sent this twice": the server
 * compares on **identity alone** — `existingVoid` looks the void up by *ticket*
 * id, not by the id in the body — so a first-time void of a ticket somebody else
 * voided a moment ago is answered `200` too (R-813 in the API's
 * `contracts/replay-asymmetry.md`). There is no wording the status could unlock
 * that this screen could stand behind: "that was already voided" would be right
 * for a replay and wrong for a race, and the two are indistinguishable from
 * here. Claiming *this* void wrote the record is the claim to avoid, and the
 * state-descriptive wording avoids it under both statuses.
 *
 * What the reader is shown is the server's own `TicketResource` — including the
 * stored reason, which under a `200` is the *first* voider's and not the words
 * just typed. That is the honest answer to "what does this ticket say now".
 */

const voidTicketSchema = z.object({ reason: requiredVoidReasonSchema });

type VoidTicketValues = z.infer<typeof voidTicketSchema>;

export function VoidTicketDialog({
  ticket,
  onClose,
  onVoided,
}: {
  /** The ticket being voided, or `null` when none is. */
  ticket: TicketResource | null;
  onClose: () => void;
  /** Handed the ticket as the API answered it — voided, and marked. */
  onVoided: (voided: TicketResource) => void;
}) {
  return (
    <Dialog
      open={ticket !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        {/* A child, so closing unmounts it: the next void carries a fresh void
            id and a fresh idempotency key rather than replaying this one. */}
        {ticket !== null && (
          <VoidTicketForm ticket={ticket} onVoided={onVoided} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function VoidTicketForm({
  ticket,
  onClose,
  onVoided,
}: {
  ticket: TicketResource;
  onClose: () => void;
  onVoided: (voided: TicketResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(voidTicketSchema),
    mode: 'onTouched',
    defaultValues: { reason: '' },
  });

  const [formError, setFormError] = useState<string | null>(null);

  // One void id and one key for as long as this form is mounted, which is one
  // void. Both are minted lazily by `useState`'s initialiser form so a re-render
  // does not mint a second and throw it away.
  const [voidId] = useState(newUuidv7);
  const [idempotencyKey] = useState(newIdempotencyKey);

  const pending = form.formState.isSubmitting;

  const handleSubmit = async ({ reason }: VoidTicketValues) => {
    // Belt and braces with the disabled button. A second submit carries the same
    // key and payload so the API replays rather than writing twice, but it would
    // still put this form into a second in-flight state for nothing.
    if (pending) return;

    setFormError(null);

    try {
      // The body's shape comes from the generated client; nothing about this
      // route is written down here. There is no `occurredAt` field on it and
      // there must not be — that one belongs to the terminal route, because a
      // till may be voiding offline and the back office never is — and no
      // `terminalId` either, because no device originated this.
      const body: TicketVoidsController_void_v1Body = { id: voidId, reason };

      onVoided(
        await client.request(TicketVoidsController_void_v1, {
          path: { id: ticket.id },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error: unknown) {
      // Every refusal this route can raise is worded from its `code` in
      // `error-messages.ts` — including the two that must read differently from
      // each other: `TICKETING_DAY_CLOSED` (too late, an owner must reopen that
      // day) and `TICKETING_VOID_IMMUTABLE` (a fault in the software). Nothing
      // is worded here, and nothing branches on a status.
      setFormError(messageForFailure(error));

      // The reason is left as it was typed. A refusal about the day or about an
      // id has nothing to do with the words, and clearing them would make
      // somebody write the sentence twice.
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          Void <span className="font-mono numeric">{ticket.number}</span>?
        </DialogTitle>
        <DialogDescription>
          <span className="font-mono text-foreground numeric">
            {formatPaise(ticket.amountPaise)}
          </span>{' '}
          was taken for this ticket. Voiding cannot be undone.
        </DialogDescription>
      </DialogHeader>

      {/* `text-foreground` on `bg-muted` — the muted-on-muted pairing misses AA
          and is the one a quiet inset panel invites. */}
      <div className="space-y-3 rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
        <p>
          This writes a void against the ticket rather than changing it. The ticket stays in the
          day&rsquo;s record, marked, so the takings still add up at close — and{' '}
          <span className="font-mono numeric">{ticket.number}</span> is never issued again, because
          the printed ticket still exists and somebody may be holding it.
        </p>
        <p>
          No money moves here and no stock is returned. A refund, if there is one, is settled at Day
          Close.
        </p>
      </div>

      <Form {...form}>
        <form
          className="space-y-5"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Reason
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11`}
                      required
                      placeholder="Sold the wrong ticket type"
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  }
                />
                <FormDescription>
                  Asked for here even though the server does not require it. It is shown on the
                  ticket&rsquo;s record and beside its row in the day&rsquo;s list, where somebody
                  who was not standing at the till will read it at close — and a cancelled sale with
                  no explanation is a question that has to be chased.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Above the submit and it stays there — a toast vanishes, and somebody
              who looked away has no way to get it back. */}
          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            {/* `destructive`, which this system reserves for the irreversible. */}
            <Button type="submit" variant="destructive" disabled={pending}>
              {pending ? 'Voiding…' : 'Void ticket'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
